/**
 * Boot-time configuration validation.
 *
 * Called once from `src/index.tsx` before the app renders. Every failure here
 * is a deployment misconfiguration that would otherwise show the user a wrong
 * or empty app, so each one throws instead of degrading.
 */
import { validateFeatureFlagEnv } from "./env";
import { FeatureFlagConfigError } from "./errors";
import { TRADING_TYPE_LIST } from "./registry";
import { isFeatureEnabled } from "./resolve";
import { getTradableTradingTypes } from "./tradingTypes";

/**
 * @throws {FeatureFlagConfigError} when
 *   - any `REACT_APP_FEATURE_*` value is not exactly `true`/`false`
 *   - a vertical is enabled but has no implementation in this build
 *   - no vertical is left to trade
 */
export const assertFeatureFlagsValid = (): void => {
  // 1. Every env value parses, including flags nothing reads on first render.
  validateFeatureFlagEnv();

  // 2. No vertical is switched on before its UI exists. Env overrides are not
  //    known at compile time, so this is the runtime equivalent of a build error.
  const enabledButUnbuilt = TRADING_TYPE_LIST.filter(
    (definition) =>
      isFeatureEnabled(definition.key) &&
      (!definition.implemented || definition.instrumentType === null)
  );
  if (enabledButUnbuilt.length) {
    throw new FeatureFlagConfigError(
      `${enabledButUnbuilt
        .map((definition) => definition.key)
        .join(", ")} is enabled but not implemented in this build. ` +
        "Disable it until its trading UI ships."
    );
  }

  // 3. Something is left to trade.
  if (!getTradableTradingTypes().length) {
    throw new FeatureFlagConfigError(
      "No trading type is enabled — the app would have nothing to trade. " +
        "Enable at least one REACT_APP_FEATURE_<KEY>."
    );
  }
};
